<script setup lang="ts">
let searchTimeout: ReturnType<typeof setTimeout> | null = null;
const isLoading = ref(false);
const isLoadingMore = ref(false);
const searchText: Ref<string> = ref('');
const searchCategories: Ref<any> = ref([]);
const searchResults: Ref<any> = ref([]);
const searchPagination: Ref<any> = ref([]);
const sportsResults: Ref<any> = ref([]);
const isSportsFilterActive: Ref<boolean> = ref(false);
const isActiveCategory: Ref<string> = ref('all');
const isPage: Ref<number> = ref(1);
const barWidth: Ref<any> = ref('');

const { searchSports } = useSportsBook();
const { t } = useTexts();
const { getGamesFromCore } = useCasino();

const isOverlayActive: Ref<boolean> = ref(false);

const sportsCategories = [
  {
    slug: 'Events',
    name: t('components.search_bar.events'),
  },
  {
    slug: 'Leagues',
    name: t('components.search_bar.leagues'),
  },
  {
    slug: 'Sports',
    name: t('components.search_bar.sports'),
  },
];

// const preventBlur = (event: any) => {
//   event.preventDefault();
// };

const handleSearch = ({ moreGames = false, category = false }) => {
  moreGames ? (isLoading.value = false) : (isLoading.value = true);
  isLoadingMore.value = true;

  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  const params = isSportsFilterActive.value
    ? {
        mode: 'word',
        query: searchText.value,
        type: isActiveCategory.value,
      }
    : [
        ['term', searchText.value],
        ['categories[]', isActiveCategory.value],
        ['sort_by', 'turnover'],
        ['sort_dir', 'desc'],
        ['page', moreGames ? isPage.value : (isPage.value = 1)],
        ['per_page', 15],
      ];

  if (searchText.value.length > 0) {
    if (!moreGames) {
      searchResults.value = [];
    }
    sportsResults.value = [];

    if (searchText.value.length >= 1) {
      searchTimeout = setTimeout(async () => {
        moreGames ? (isLoading.value = false) : (isLoading.value = true);
        try {
          const res = isSportsFilterActive.value
            ? await searchSports(params)
            : await getGamesFromCore(params);

          if (isSportsFilterActive.value) {
            searchCategories.value = sportsCategories;
            if (isActiveCategory.value === 'Events') {
              sportsResults.value = res.events ?? [];
            } else if (isActiveCategory.value === 'Leagues') {
              sportsResults.value = res.leagues ?? [];
            } else if (isActiveCategory.value === 'Sports') {
              sportsResults.value = res.sports ?? [];
            }
          } else {
            if (moreGames) {
              isLoadingMore.value = true;
              searchResults.value = [...searchResults.value, ...res.data];
            } else {
              searchResults.value = res.data;
            }

            if (!category) {
              searchCategories.value = res.categories;
            }

            searchPagination.value = {
              from: res.from,
              to: res.to,
              total: res.total,
              lastPage: res.last_page,
              currentPage: res.current_page,
              nextPage:
                res.current_page < res.last_page
                  ? res.current_page + 1
                  : res.last_page,
            };
            calculateWidthStyle(searchPagination.value);
          }
        } catch (error) {
          console.error('Erro ao buscar jogos:', error);
        } finally {
          isLoading.value = false;
          isLoadingMore.value = false;
        }
      }, 800);
    } else {
      sportsResults.value = [];
      searchResults.value = [];
      isLoading.value = false;
      isLoadingMore.value = false;
    }
  } else {
    isLoadingMore.value = false;
    clearSearch();
  }
};

const setActiveCategory = (category: string) => {
  isActiveCategory.value = category;
  handleSearch({ moreGames: false, category: true });
};

const getMoreGames = (page: number) => {
  isPage.value = page;
  handleSearch({ moreGames: true, category: true });
};

const calculateWidthStyle = (data: any) => {
  barWidth.value = { width: `${(data.to * 100) / data.total}%` };
};

const clearSearch = () => {
  searchCategories.value = [];
  searchResults.value = [];
  searchPagination.value = [];
  isActiveCategory.value = isSportsFilterActive.value ? 'Events' : 'all';
  isLoading.value = false;
};

const handleOverlayStatus = (overlayStatus: boolean) => {
  isOverlayActive.value = overlayStatus;
};

function setSportsFilterActive(val: boolean): void {
  isSportsFilterActive.value = val;
  isActiveCategory.value = val ? 'Events' : 'all';
  if (val) {
    searchCategories.value = sportsCategories;
  }
  if (searchText.value) handleSearch({ moreGames: false });
}

// Watch para detectar mudanças no searchText e disparar a pesquisa
watch(searchText, () => {
  handleSearch({ moreGames: false, category: false });
});

document.addEventListener('touchmove', () => {
  document
    .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
    .forEach((el) => el.blur());
});
</script>

<template>
  <div class="gameSearchBar">
    <BaseSearchInput
      v-model="searchText"
      :able-to-overlay="true"
      :is-loading="isLoading || isLoadingMore"
      :scroll-when-focus="true"
      @clear-search="clearSearch"
      @overlay-status="
        (overleyStatus: boolean) => handleOverlayStatus(overleyStatus)
      "
      @search="handleSearch({ moreGames: false })"
      @set-sports-filter-active="setSportsFilterActive"
      @update:model-value="() => handleSearch({ moreGames: false })"
    />
    <!-- :less-chars="lessCharsParent" -->
    <BaseSearchResults
      :calculate-width-style="barWidth"
      :is-active-category="isActiveCategory"
      :is-loading="isLoading"
      :is-loading-more="isLoadingMore"
      :is-overlay-active="isOverlayActive"
      :is-sports-tab-active="isSportsFilterActive"
      :search-categories="searchCategories"
      :search-pagination="searchPagination"
      :search-results="searchResults"
      :search-text="searchText"
      :sports-results="sportsResults"
      @get-more-games="getMoreGames"
      @set-active-category="setActiveCategory"
    />
  </div>
</template>

<style scoped lang="scss">
.gameSearchBar {
  @apply relative my-4;
  min-height: 36px;
}
</style>
